var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { apiUpdateRfpOfferDraft } from '@/actions/rfpProOfferActions';
import { RfpProOfferView } from '@/components/pages/rfp/professional/RfpProOfferView';
import Layout from '@/container/Layout';
import { notify } from '@/helpers/action';
import { useToastUnmountClear } from '@/helpers/use/useToast';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { useUnmountRef } from '@/hooks/lifecycle/useUnmountRef';
import { useRfpMemberAddModal } from '@/hooks/pages/rfp/useRfpMemberAddModal';
import { addRfpProOfferMemberAction, updateRfpProOfferAction, updateRfpProOfferMemberAction, updateRfpProOfferAssignAction, updateRfpProOfferPaymentAction, } from '@/pages/rfp/professional/offer/RfpProOfferRoot/RfpProOfferRootReducer';
import { wrapDispatch } from '@/utils/actions/dispatch';
import { uploadFilePromise } from '@/utils/file/uploadFile';
import { getIsPublic, getDirectOfferDraftValues, getPublicOfferDraftValues, } from '@/utils/resources/rfp/rfpProOfferSave';
import { isRfpRoleAdmin } from '@/utils/resources/rfp/rfpRoles';
import { professionalRfpOfferPaymentPath } from '@/utils/router/links';
const title = '提案内容を入力';
/**
 * コンサルタント：案件提案提出のページ全体を表示する
 */
export const RfpProOfferPage = (props) => {
    const { changeStepValue, formDispatch, formValues, lockId, rfp } = props;
    const { id } = rfp;
    const dispatch = useDispatch();
    const unmountRef = useUnmountRef();
    const history = useNavigate();
    /**
     * Form 更新処理
     */
    const updateFormState = useCallback((offerFormValues) => {
        formDispatch(updateRfpProOfferAssignAction(offerFormValues.assign));
        formDispatch(updateRfpProOfferAction(offerFormValues.offer));
        formDispatch(updateRfpProOfferMemberAction(offerFormValues.rfp_offer_members));
    }, [formDispatch]);
    /** アップロード中のファイル件数 */
    const [uploadingFileCount, changeUploadingFileCount] = useState(0);
    /**
     * ファイルアップロード処理
     */
    const uploadFile = useCallback((uploadFileValue) => __awaiter(void 0, void 0, void 0, function* () {
        changeUploadingFileCount((prev) => prev + 1);
        try {
            return yield uploadFilePromise({
                dispatch,
                file: uploadFileValue,
                showProgress: true,
                unmountRef,
            });
        }
        finally {
            if (!unmountRef.current) {
                changeUploadingFileCount((prev) => prev - 1);
            }
        }
    }), [dispatch, changeUploadingFileCount, unmountRef]);
    /**
     * メンバー追加時の Form 更新処理
     * @params values メンバー追加モーダルの FormValues
     */
    const onSubmitMemberAddModal = useCallback((values) => {
        formDispatch(addRfpProOfferMemberAction(Object.assign(Object.assign({}, values), { involvement: null, role: null })));
    }, [formDispatch]);
    /** メンバー追加モーダル処理 */
    const addModal = useRfpMemberAddModal({
        dispatch,
        id,
        onSubmit: onSubmitMemberAddModal,
        unmountRef,
    });
    /**
     * メンバー追加ボタンクリック時の処理
     */
    const onClickAddMember = useCallback((values) => {
        updateFormState(values);
        addModal.showModal();
    }, [addModal, updateFormState]);
    /**
     * 請求書送付先のデフォルトに管理者を設定
     */
    const setBillingInformation = useCallback((offerFormValues) => {
        var _a, _b;
        const adminMember = offerFormValues.rfp_offer_members.find((member) => isRfpRoleAdmin(member.role), []);
        // 責任者がいない場合（バリデーションにより必ずいる想定）
        if (!adminMember) {
            return;
        }
        /** 管理者を除いた 請求書送付先の値 */
        const newBillingInformation = (((_a = formValues === null || formValues === void 0 ? void 0 : formValues.payment) === null || _a === void 0 ? void 0 : _a.billing_information) || []).filter((billing) => billing.email !== adminMember.email &&
            billing.first_name !== adminMember.first_name &&
            billing.last_name !== adminMember.last_name);
        // 1件目に管理者の値を設定
        // NOTE: バリデーションにより email が - になることはない想定
        newBillingInformation.unshift({
            email: adminMember.email || '-',
            first_name: adminMember.first_name || '-',
            last_name: adminMember.last_name || '-',
        });
        formDispatch(updateRfpProOfferPaymentAction({
            billing_information: newBillingInformation,
            options: ((_b = formValues === null || formValues === void 0 ? void 0 : formValues.payment) === null || _b === void 0 ? void 0 : _b.options) || null,
        }));
    }, [formDispatch, formValues]);
    /**
     * 下書き保存
     */
    const onSubmitDraft = useCallback((offerFormValues) => __awaiter(void 0, void 0, void 0, function* () {
        const isPublic = getIsPublic(rfp.rfp_type, offerFormValues);
        const data = {
            json: isPublic
                ? getPublicOfferDraftValues(offerFormValues, formValues)
                : getDirectOfferDraftValues(offerFormValues, formValues),
            path: { id, lock_id: lockId },
        };
        yield wrapDispatch(dispatch, apiUpdateRfpOfferDraft(data, {}));
        notify('下書きに保存しました');
    }), [dispatch, formValues, id, lockId, rfp]);
    /**
     * submit
     */
    const onSubmit = useCallback((offerFormValues) => __awaiter(void 0, void 0, void 0, function* () {
        // 下書き保存
        if (offerFormValues.is_draft) {
            changeStepValue({ offer: false });
            yield onSubmitDraft(offerFormValues);
            return;
        }
        // form 値更新
        updateFormState(offerFormValues);
        // 請求書送付先のデフォルトに管理者を設定
        setBillingInformation(offerFormValues);
        // 支払い画面へ遷移
        changeStepValue({ offer: true });
        history.push(professionalRfpOfferPaymentPath(id), {
            state: { lockId },
        });
    }), [
        changeStepValue,
        id,
        history,
        lockId,
        onSubmitDraft,
        setBillingInformation,
        updateFormState,
    ]);
    /** OfferForm 初期値 */
    const initialValues = useMemo(() => ({
        assign: formValues.assign,
        offer: formValues.offer,
        rfp_offer_members: formValues.rfp_offer_members,
    }), [formValues]);
    /** 案件メンバー 追加済のメールアドレス */
    const registeredEmails = useMemo(() => formValues.rfp_offer_members.map((member) => member.email), [formValues]);
    // トースト表示制御
    useToastUnmountClear(dispatch);
    return (_jsxs(Layout, Object.assign({ mainClassName: 'l-main rfp_list_container rfp_common_container' }, { children: [_jsx(Helmet, { children: _jsx("title", { children: `${title}｜PROFFIT` }) }), _jsx(RfpProOfferView, { clearIsMissingRole: addModal.clearIsMissingRole, enableDomains: rfp.enable_domains, hasUploadingFile: uploadingFileCount > 0, initialValues: initialValues, interviewSchedule: rfp.interview_schedule, isMissingRole: addModal.isMissingRole, isShowMemberAddModal: addModal.isShow, lockId: lockId, onClickAddMember: onClickAddMember, onClickCancelOfMemberAddModal: addModal.hideModal, onSubmit: onSubmit, onSubmitOfMemberAddModal: addModal.onSubmit, registeredEmails: registeredEmails, rfpId: id, rfpName: rfp.name, rfpType: rfp.rfp_type, title: title, uploadFile: uploadFile })] })));
};
